import { useTranslation } from '@shared/localization';
import { PropsWithChildren } from 'react';

import { PageLayout, ProgressBarButton } from '@/components/common';
import { AlertSuccess } from '@/components/common/alerts';
import { PaymentIntentFlow } from '@/schemas/PaymentIntent';
import PaymentStore from '@/services/payments/PaymentStore';
import { redirect } from '@/utils/redirect';

interface PaymentSuccessfulProps extends PropsWithChildren {
  readonly orderId?: string;
  readonly title: string;
  readonly successUrl?: string;
}

export function PaymentSuccessful({
  orderId,
  title,
  successUrl,
  children,
}: PaymentSuccessfulProps) {
  const { t } = useTranslation();

  const description =
    PaymentStore.flow === PaymentIntentFlow.Moto
      ? t('Close the window')
      : undefined;

  /*
      In MPM we have a special case where the CIT(!!!) flow is initiated from MPM.
      This is needed to handle case when a successful CIT transaction is needed for subsequesnt MIT.
      Use case is: MPM operator wants to create a new payment method for a customer, but the customer
      has no CIT transaction yet, so PaymentService is creting for the user a link with pid that user
      will use to pay (add card). But in this case there is no need to show the button to redirect user to the successUrl,
      because the user is already on the MPM page and the successUrl is not needed.
  */
  const citWasInitiatedFromMpm =
    successUrl === '' && PaymentStore.flow === PaymentIntentFlow.Cit;

  return (
    <PageLayout
      headerDescription={
        orderId
          ? t('Order: {{orderNumber}}', { orderNumber: orderId })
          : undefined
      }
    >
      <AlertSuccess
        title={title}
        description={description ?? undefined}
      >
        {children}
      </AlertSuccess>
      {successUrl !== undefined &&
        PaymentStore.flow !== PaymentIntentFlow.Moto &&
        !citWasInitiatedFromMpm && (
          <ProgressBarButton
            extraClassName="mx-sm md:mx-md"
            onClick={() => {
              redirect(successUrl);
            }}
          />
        )}
    </PageLayout>
  );
}
